/**
 * Hospitality Linen Program — API client and shared types.
 *
 * Pricing is computed server-side (POST /api/linen/quote) so the pricing
 * configuration — margins, reserves, fees — never ships to the browser.
 * This module only holds the input/result types, the fetch helpers, and
 * currency formatting.
 */

export type QualityKey = 'midscale' | 'luxury';
export type LaundryMethod = 'onsite' | 'offsite';
export type RotationLevel = 2 | 3 | 4;

export interface BedCounts {
  king: number;
  queen: number;
  full: number;
  twin: number;
  sofa: number;
}

export interface CalculatorInputs {
  quality: QualityKey;
  bedrooms: number;
  beds: BedCounts;
  bathrooms: number;
  maxOccupancy: number;
  rotations: RotationLevel;
  laundryMethod: LaundryMethod;
  turnoversPerMonth: number;
  poolTowels: number;
  beachTowels: number;
}

export type ComplexityLabel = 'Light' | 'Medium' | 'Heavy' | 'Very Heavy';

export interface InventoryBreakdown {
  beds: number;
  /** Fixed per-bathroom items plus perGuest towel sets × max occupancy. */
  bathrooms: number;
  optionalTowels: number;
}

export interface MonthlyBreakdown {
  capitalRecovery: number;
  managementFee: number;
  replacementReserve: number;
  /** Expected turnover revenue credited against the recovery requirement. */
  turnoverRevenueCredit: number;
}

export interface LinenItem {
  item: string;
  quantity: number;
}

/** What is physically supplied — types and quantities only, never prices. */
export interface LinenConfiguration {
  bedrooms: LinenItem[];
  bathrooms: LinenItem[];
}

export interface QuoteResult {
  totalBeds: number;
  inventoryValue: number;
  monthlyBreakdown: MonthlyBreakdown;
  /** One-time signup fee; 0 when the program has none. */
  setupFee: number;
  inventoryBreakdown: InventoryBreakdown;
  linenConfiguration: LinenConfiguration;
  monthlyPrice: number;
  complexityScore: number;
  complexityLabel: ComplexityLabel;
  exceptionalComplexity: boolean;
  catalogKey: string;
  usedFallbackPricing: boolean;
  includedBeds: number;
  extraBeds: number;
  includedOccupancy: number;
  extraGuests: number;
  turnoverFee: number;
  monthlyTotal: number;
  annualTotal: number;
  upfrontInvestmentAvoided: number;
  qualityLabel: string;
}

export interface QualityOption {
  key: QualityKey;
  label: string;
  examples: string[];
}

export const defaultInputs: CalculatorInputs = {
  quality: 'midscale',
  bedrooms: 2,
  beds: { king: 1, queen: 1, full: 0, twin: 0, sofa: 0 },
  bathrooms: 2,
  maxOccupancy: 4,
  rotations: 3,
  laundryMethod: 'onsite',
  turnoversPerMonth: 4,
  poolTowels: 0,
  beachTowels: 0,
};

/** Display fallback when the options endpoint is unreachable. */
export const fallbackQualityOptions: QualityOption[] = [
  {
    key: 'midscale',
    label: 'Midscale Hospitality',
    examples: ['Hampton Inn', 'Holiday Inn Express', 'Fairfield', 'Courtyard', 'Professional Airbnb'],
  },
  {
    key: 'luxury',
    label: 'Luxury Hospitality',
    examples: ['Marriott', 'Westin', 'JW Marriott', 'Luxury Vacation Rental'],
  },
];

const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export const fetchQualityOptions = async (): Promise<QualityOption[]> => {
  const response = await fetch(`${apiBase}/api/linen/options`);
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Failed to load options');
  return data.qualityOptions;
};

export const fetchLinenQuote = async (
  inputs: CalculatorInputs,
  signal?: AbortSignal
): Promise<QuoteResult> => {
  const response = await fetch(`${apiBase}/api/linen/quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(inputs),
    signal,
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to compute quote');
  }
  return data.quote as QuoteResult;
};

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const formatCurrency = (value: number): string => currency.format(value);
